import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserRoleManager } from './UserRoleManager';
import { UserDetails } from './UserDetails';
import { UserOrganizations } from './UserOrganizations';
import { UserHabits } from './UserHabits';
import { UserFollowup } from './UserFollowup';
import { UserVolunteering } from './UserVolunteering';
import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useLanguage } from '@/contexts/LanguageContext';
import type { User } from '@/types';

interface UserRoleDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate?: () => void;
}

export function UserRoleDialog({ 
  user, 
  open, 
  onOpenChange,
  onUpdate 
}: UserRoleDialogProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(user);
  const [activeTab, setActiveTab] = useState('details');
  const { t, dir } = useLanguage();

  useEffect(() => {
    if (!user?.id) return;

    // Subscribe to user document changes
    const unsubscribe = onSnapshot(doc(db, 'users', user.id), (doc) => {
      if (doc.exists()) {
        setCurrentUser({ id: doc.id, ...doc.data() } as User);
      }
    });

    return () => unsubscribe();
  }, [user?.id]);

  // Reset tab when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveTab('details');
    }
  }, [open]);

  const handleUpdate = () => {
    if (onUpdate) {
      onUpdate();
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[93vh] w-[95vw] overflow-hidden" dir={dir}>
        <DialogHeader className={`pb-4 ${dir === 'rtl' ? '!text-right' : '!text-left'}`}>
          <DialogTitle className="text-2xl">{t('dashboard.admin.users.roleManager.title')}</DialogTitle>
          <DialogDescription className="text-base">
            {t('dashboard.admin.users.roleManager.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
            <TabsList className={`flex w-full mb-6 flex-shrink-0 ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'}`}>
              <TabsTrigger value="details" className="text-sm font-medium flex-1">{t('dashboard.admin.users.tabs.details')}</TabsTrigger>
              <TabsTrigger value="roles" className="text-sm font-medium flex-1">{t('dashboard.admin.users.tabs.roles')}</TabsTrigger>
              <TabsTrigger value="organizations" className="text-sm font-medium flex-1">{t('dashboard.admin.users.tabs.organizations')}</TabsTrigger>
              <TabsTrigger value="habits" className="text-sm font-medium flex-1">{t('dashboard.admin.users.tabs.habits')}</TabsTrigger>
              <TabsTrigger value="volunteering" className="text-sm font-medium flex-1">{t('dashboard.admin.users.tabs.volunteering') || 'Volunteering'}</TabsTrigger>
              <TabsTrigger value="followup" className="text-sm font-medium flex-1">{t('dashboard.admin.users.tabs.followup')}</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-hidden min-h-0" style={{ height: 'calc(100vh - 270px)' }}>
              <TabsContent value="details" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2" style={{ maxHeight: 'calc(100vh - 320px)' }}>
                <UserDetails 
                  user={currentUser}
                  onUpdate={handleUpdate}
                />
              </TabsContent>

              <TabsContent value="roles" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2" style={{ maxHeight: 'calc(100vh - 320px)' }}>
                <UserRoleManager 
                  user={currentUser}
                  onUpdate={handleUpdate}
                />
              </TabsContent>

              <TabsContent value="organizations" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2" style={{ maxHeight: 'calc(100vh - 320px)' }}>
                <UserOrganizations 
                  user={currentUser}
                  onUpdate={handleUpdate}
                />
              </TabsContent>

              <TabsContent value="habits" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2" style={{ maxHeight: 'calc(100vh - 320px)' }}>
                <UserHabits 
                  user={currentUser}
                  onUpdate={handleUpdate}
                />
              </TabsContent>

              <TabsContent value="volunteering" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2" style={{ maxHeight: 'calc(100vh - 320px)' }}>
                <UserVolunteering 
                  user={currentUser}
                  onUpdate={handleUpdate}
                />
              </TabsContent>

              <TabsContent value="followup" className="h-full overflow-y-auto data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-left-2" style={{ maxHeight: 'calc(100vh - 320px)' }}>
                <UserFollowup 
                  user={currentUser}
                  onUpdate={handleUpdate}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}